import { useEffect, useState } from 'react';
import { Pencil, ShieldAlert, Save, X } from 'lucide-react';
import {
  UniversalPage,
  Card,
  Button,
  Input,
  Textarea,
  NotificationCard,
  Modal,
  ModalBody,
  ModalFooter,
  StatusBadge,
  useToast,
} from '@sighthost/ui';
import {
  adminSettingsService,
  type SystemSettingRow,
} from '@/domains/settings/services/adminSettingsService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { formatDateTime } from '@/shared/utils/format';

function valueToDisplay(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return `"${v}"`;
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  return JSON.stringify(v, null, 2);
}

function valueToEditable(v: unknown): string {
  return JSON.stringify(v ?? null, null, 2);
}

export default function SystemSettingsPage() {
  const { user } = useAuth();
  const role = (user?.app_metadata as { role?: string } | undefined)?.role;
  const isSuperAdmin = role === 'super_admin';

  const { toast } = useToast();
  const [rows, setRows] = useState<SystemSettingRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SystemSettingRow | null>(null);
  const [editText, setEditText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function reload() {
    adminSettingsService
      .listSystemSettings()
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }

  useEffect(reload, []);

  function openEdit(row: SystemSettingRow) {
    setEditing(row);
    setEditText(valueToEditable(row.value));
    setParseError(null);
  }

  async function save() {
    if (!editing) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(editText);
    } catch (e) {
      setParseError(`Invalid JSON: ${(e as Error).message}`);
      return;
    }
    setParseError(null);
    setSaving(true);
    try {
      await adminSettingsService.updateSystemSetting(editing.key, parsed);
      toast({ tone: 'success', title: 'Setting updated', body: editing.key });
      setEditing(null);
      reload();
    } catch (e) {
      toast({
        tone: 'danger',
        title: 'Update failed',
        body: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <UniversalPage
      title="System settings"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Settings', href: '/admin/settings' },
        { label: 'System' },
      ]}
    >
      {!isSuperAdmin && (
        <NotificationCard
          tone="info"
          title="Read-only view"
          body="Only super_admin can edit system settings. Promote your account via SQL or contact a super_admin to change values."
        />
      )}

      <Card interactive={false} className="border-warning/30 bg-warning/5">
        <div className="flex items-start gap-3 p-4 text-sm">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div>
            <p className="font-medium text-foreground">Operational kill switches and tunables</p>
            <p className="mt-1 text-muted-foreground">
              These rows control runtime behaviour: <code>provisioning_paused</code> halts the
              runner, <code>billing_cron_enabled</code> stops invoice generation,
              <code>domains_balance_floor</code> blocks paid registrar mutations below the threshold.
              Changes apply on the next cron tick / EF invocation. <strong>No undo.</strong>
            </p>
          </div>
        </div>
      </Card>

      {error && <NotificationCard tone="danger" title="Could not load settings" body={error} />}

      <Card interactive={false}>
        <div className="divide-y divide-border">
          {rows.map((row) => (
            <div key={row.key} className="space-y-2 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <code className="text-sm font-semibold text-foreground">{row.key}</code>
                  {row.description && (
                    <p className="mt-1 text-xs text-muted-foreground">{row.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge tone="info">{typeof row.value}</StatusBadge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(row)}
                    disabled={!isSuperAdmin}
                    title={!isSuperAdmin ? 'super_admin only' : undefined}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                </div>
              </div>
              <pre className="overflow-x-auto rounded bg-muted p-2 text-xs text-foreground">
                {valueToDisplay(row.value)}
              </pre>
              {/* F-16. Was `updated_by.slice(0, 8)…` — a truncated uuid nobody
                  can read, on a page that warns "No undo". It never rendered
                  anyway: `updated_by` was NULL on all 23 rows because the only
                  writer never set it. A trigger now stamps it from auth.uid()
                  and the view resolves it to an email, so this becomes real
                  the first time anyone edits a setting.
                  Says "not recorded" rather than nothing, because a blank
                  looks like an answer and this is the absence of one. */}
              <p className="text-xs text-muted-foreground">
                Last updated {formatDateTime(row.updated_at)}{' '}
                {row.updated_by_email ? (
                  <>by <span className="text-foreground">{row.updated_by_email}</span></>
                ) : (
                  <span className="italic">· author not recorded</span>
                )}
              </p>
            </div>
          ))}
          {rows.length === 0 && !error && (
            <p className="p-8 text-center text-sm text-muted-foreground">Loading…</p>
          )}
        </div>
      </Card>

      <Modal
        open={editing !== null}
        onOpenChange={(v) => !v && setEditing(null)}
        title={`Edit ${editing?.key ?? ''}`}
        description={editing?.description ?? undefined}
        size="wide"
      >
        <ModalBody>
          <p className="mb-2 text-xs text-muted-foreground">
            Value must be valid JSON. Strings include quotes; booleans are <code>true</code> /{' '}
            <code>false</code>; objects use <code>{`{ }`}</code>.
          </p>
          {/* F-15. This was an <Input> — a SINGLE-LINE field — while the modal
              loads `JSON.stringify(value, null, 2)`, i.e. pretty-printed
              multi-line JSON. Nine of the 23 settings hold an object or array
              and the longest is 640 characters, so the settings that matter
              most (`domains_pricing`, `plan_disk_map`,
              `cron_manual_trigger_allowlist`) could not actually be edited
              here at all. Textarea has been in the design system throughout. */}
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            disabled={saving}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            rows={14}
            className={`font-mono text-xs ${
              parseError ? 'border-danger focus-visible:ring-danger' : ''
            }`}
            aria-invalid={Boolean(parseError)}
          />
          {parseError && (
            <p className="mt-2 flex items-start gap-2 text-xs text-danger">
              <X className="mt-0.5 h-3 w-3 shrink-0" />
              {parseError}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving} disabled={saving}>
            <Save className="h-4 w-4" /> Save
          </Button>
        </ModalFooter>
      </Modal>
    </UniversalPage>
  );
}
